import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from '../static/styles/Layouts.module.css';
import logoImg from '../static/img/eslLogo.png';

import { getUserProfile, getUserNotifCount } from '../api/profile';

import homeIcon from '../static/img/Sidebar/iconHome.svg'
import dailyTaskIcon from '../static/img/Sidebar/iconDailyTasks.svg'
import tournamentIcon from '../static/img/Sidebar/iconTournaments.png'
import rewardsIcon from '../static/img/Sidebar/iconRewards.svg'
import leaderboardIcon from '../static/img/Sidebar/iconLeaderboard.svg'
import highlightsIcon from '../static/img/Sidebar/iconHighlights.svg'
import completedIcon from '../static/img/Sidebar/iconCompletedTasks.svg'
import settingsIcon from '../static/img/Sidebar/iconSettings.svg'
import defaultAvatarIcon from '../static/img/Sidebar/Avatar.png'
import notificationIcon from '../static/img/MobileAppbar/notificationIcon.svg'

import instagramIcon from '../static/img/social/instagram.svg'
import facebookIcon from '../static/img/social/facebook.svg'
import youtubeIcon from '../static/img/social/youtube.svg'
import twitterIcon from '../static/img/social/twitter.svg'
import coinsIcon from '../static/img/Sidebar/iconCheckinCoins.png'
import DailyCheckin from '../views/DailyCheckin';
import { isAuthenticated } from '../api/auth';

const LayoutDesktop = ({ children, profileData }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isCheckinOpen, setIsCheckinOpen] = useState(false);
  const [userProfile, setUserProfile] = useState({});
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserProfile = async () => {
      const profile = await getUserProfile();
      setUserProfile(profile);
    };
    fetchUserProfile();
  }, []);

  useEffect(() => {
    const fetchNotificationCount = async () => {
      const result = await getUserNotifCount();
      if (result.success && result.notificationsCount !== undefined) {
        setUnreadNotifications(result.notificationsCount);
      }
    };
    fetchNotificationCount();

    const handleRefreshNotifications = () => {
      fetchNotificationCount();
    };
    window.addEventListener('notificationsRead', handleRefreshNotifications);

    return () => {
      window.removeEventListener('notificationsRead', handleRefreshNotifications);
    };
  }, []);

  const menuItems = [
    { icon: homeIcon, label: t('nav.homepage'), action: '/' },
    { icon: dailyTaskIcon, label: t('nav.daily_tasks'), action: '/tasks' },
    { icon: tournamentIcon, label: t('nav.tournaments'), action: '/tournaments' },
    { icon: dailyTaskIcon, label: t('nav.gaming_tasks'), action: '/daily-gaming-tasks' }, // New entry
    { icon: rewardsIcon, label: t('nav.rewards'), action: '/rewards' },
    { icon: leaderboardIcon, label: t('nav.leaderboard'), action: '/leaderboard' },
    { icon: highlightsIcon, label: t('nav.highlights'), action: '/highlights' },
    { icon: completedIcon, label: t('nav.completed_tasks'), action: '/completed' },
    { icon: notificationIcon, label: t('nav.notifications'), action: '/notifications', badge: true },
    // { icon: settingsIcon, label: "Settings", action: '/settings' },
  ];

  const socialLinks = [
    { icon: instagramIcon, url: '#', label: 'Instagram' },
    { icon: facebookIcon, url: '#', label: 'Facebook' },
    { icon: youtubeIcon, url: '#', label: 'YouTube' },
    { icon: twitterIcon, url: '#', label: 'Twitter' },
  ];

  const avatarUrl = userProfile?.profileImage || defaultAvatarIcon;
  let username = userProfile?.name || userProfile?.username || t('guest_user');
  if (username.length > 7) {
    username = `${username.substring(0, 7)}...`;
  }

  const handleCheckIn = () => {
    if (isAuthenticated()) {
      setIsCheckinOpen(true);
    }
  }
  const handleNavigation = (action) => {
    if (typeof action === 'function') {
      action();
    } else if (typeof action === 'string') {
      navigate(action);
    }
  };

  return (
    <div className={styles.layout}>
      <div className={styles.layoutContainer}>
        <nav className={styles.sidebar}>
          <img src={logoImg} alt="ESL" className={styles.logo} />

          {/* Profile Section */}
          <Link to="/profile" className={styles.sidebarProfile}>
            <img src={avatarUrl} alt="" className={styles.profileImage}
              onError={e => { e.target.onerror = null; e.target.src = defaultAvatarIcon; }} />
            <div className={styles.profileInfo}>
              <span className={styles.userName}>{username}</span>
              <span className={styles.profileLabel}>{t('profile')}</span>
            </div>
          </Link>

          <ul className={styles.menuList}>
            {menuItems.map((item) => (
              <li key={item.label}>
                <Link
                  to={item.action}
                  className={`${styles.navItem} ${item.action === '/tournaments'
                    ? (location.pathname === '/tournaments' || location.pathname.startsWith('/tournament/')) ? styles.navItemActive : ''
                    : location.pathname === item.action ? styles.navItemActive : ''
                    }`}
                >
                  <div className={styles.navItemContent}>
                    <img src={item.icon} alt="" className={styles.navIcon} />
                    <span className={styles.navText}>{item.label}</span>
                    {item.badge && unreadNotifications > 0 && (
                      <span className={styles.notificationBadge}>{unreadNotifications}</span>
                    )}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
          <div className={styles.rewardsContainer} onClick={() => { handleCheckIn() }} >
            <img src={coinsIcon} alt="" className={styles.goldPile} />
            <button className={styles.checkInButton}>
              {t('nav.check_in_rewards')}
              {/* <span className={styles.notificationBadge}>2</span> */}
            </button>
          </div>
        </nav>
        <DailyCheckin isOpen={isCheckinOpen} onClose={() => setIsCheckinOpen(false)} />

        <main className={styles.mainContent}>
          <div className={styles.mainContentInner}>
            {children}
            {(location.pathname !== '/puzzle-mania' && location.pathname !== '/space-football') && (
              <footer className={styles.footer}>
                <div className={styles.footerLeft}>
                  <p className={styles.footerText}>
                    {t('footer.unleash_potential')}
                    <br />
                    {t('footer.rights_reserved')}
                  </p>
                  <img src={logoImg} alt="ESL" className={styles.footerLogo} />
                </div>
                <div className={styles.footerRight}>
                  <p className={styles.followUsText}>{t('footer.follow_us')}</p>
                  <div className={styles.socialIcons}>
                    {socialLinks.map((social) => (
                      <a
                        key={social.label}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src={social.icon}
                          alt={social.label}
                          className={styles.socialIcon}
                        />
                      </a>
                    ))}
                  </div>
                </div>
              </footer>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default LayoutDesktop;